<script setup lang="ts">
import { APP_NAME } from "@/assets/js/appConfig";
import { marked } from "marked";

definePage({
	name: "TermsOfUsePage",
});
const CONTACT_EMAIL = "contact@olderone.com";

const privacyPolicyMarkdown = `
# Terms of Use

**Effective Date:** July 2, 2026

Welcome to **${APP_NAME}**. By accessing or using our website, you agree to these Terms of Use.

If you do not agree, please do not use the website.

## 1. About the Website

${APP_NAME} provides informational and entertainment content through quick comparison games. The website does not require user registration or login.

## 2. Use of the Website

You agree to use the website lawfully and not to:

- interfere with the operation or security of the site
- attempt unauthorized access to the site or its infrastructure
- scrape, copy, or misuse site content beyond what is permitted by law
- use the site in a way that could damage, disable, or impair its availability

## 3. Third-Party Content Notices

Some content made available through the website is sourced from the **TMDB API**.

**This product uses the TMDB API but is not endorsed or certified by TMDB.**

TMDB-related names, logos, and content remain subject to TMDB’s own rights, branding rules, and applicable terms.

Cover art is provided via the Cover Art Archive, a joint project of Internet Archive and MusicBrainz. Images are copyrighted by their respective copyright owners.

## 4. Intellectual Property

Unless otherwise stated, the website design, code, branding, and original text content of ${APP_NAME} are owned by us or used with permission.

Third-party content, including content sourced from TMDB, may be subject to separate rights held by third parties.

## 5. Accuracy and Availability

We try to keep the website accurate and available, but we do not guarantee that the site or any content will always be complete, error-free, or uninterrupted.

Content from third-party sources may change, be corrected, or be removed without notice.

## 6. No Professional Advice

The content on this website is provided for general informational or entertainment purposes only and does not constitute legal, financial, medical, or other professional advice.

## 7. Limitation of Liability

To the maximum extent permitted by law, we are not liable for indirect, incidental, special, or consequential damages arising from your use of the website.

## 8. External Services

The website may contain links to or rely on third-party services. We are not responsible for third-party websites, services, or their policies.

## 9. Changes to the Website or Terms

We may modify the website or these Terms of Use at any time. Updated terms will be posted on this page with a revised effective date.

## 10. Contact

If you have questions about these Terms, contact:

**${CONTACT_EMAIL}**
`;

const privacyPolicyHtml = computed(() => marked.parse(privacyPolicyMarkdown));
</script>

<template>
	<section class="legal-page">
		<div class="overlay"></div>

		<RouterLink
			to="/"
			class="category-back-link"
		>
			← Back
		</RouterLink>

		<div class="legal-card">
			<div
				class="markdown-body"
				v-html="privacyPolicyHtml"
			></div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.legal-page {
	position: relative;
	min-height: 100dvh;
	padding: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background: radial-gradient(
			circle at center,
			#2a2a2a 0%,
			#171717 46%,
			#070707 100%
		),
		#101010;
	// background-image: url("https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=80");
	// background-size: cover;
	// background-position: center;
	// background-repeat: no-repeat;
	// isolation: isolate;
}

.overlay {
	position: absolute;
	inset: 0;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 0.04) 0%,
		rgba(0, 0, 0, 0.08) 42%,
		rgba(0, 0, 0, 0.42) 100%
	);
	z-index: 0;
}
.legal-card {
	position: relative;
	z-index: 1;
	width: 100%;
	max-width: 920px;
	max-height: calc(100dvh - 40px);
	overflow-y: auto;
	overflow-x: hidden;
	padding: 28px 24px;
	border-radius: 24px;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	scrollbar-width: thin;
	scrollbar-color: rgba(255, 255, 255, 0.18) transparent;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.16);
		border-radius: 999px;
		border: 2px solid transparent;
		background-clip: padding-box;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: rgba(255, 255, 255, 0.24);
		border: 2px solid transparent;
		background-clip: padding-box;
	}
}

.markdown-body {
	color: rgba(255, 255, 255, 0.9);
	line-height: 1.7;
	font-size: 15px;

	:deep(h1) {
		margin: 0 0 20px;
		font-size: clamp(32px, 5vw, 48px);
		line-height: 1;
		font-weight: 900;
		letter-spacing: -0.03em;
		color: $text-color-white;
	}

	:deep(h2) {
		margin: 28px 0 12px;
		font-size: 22px;
		font-weight: 800;
		color: $text-color-white;
	}

	:deep(p) {
		margin: 0 0 14px;
	}

	:deep(ul) {
		margin: 0 0 16px 18px;
		padding: 0;
	}

	:deep(li) {
		margin-bottom: 8px;
	}

	:deep(strong) {
		color: $text-color-white;
		font-weight: 800;
	}

	:deep(a) {
		color: $text-color-white;
	}
}

@media (max-width: 640px) {
	.legal-page {
		align-items: flex-start;
		padding: 76px 16px calc(24px + env(safe-area-inset-bottom));
		overflow-y: auto;
	}

	.legal-card {
		max-height: none;
		padding: 22px 18px;
		border-radius: 20px;
		overflow: visible;
	}

	.markdown-body {
		font-size: 14px;

		:deep(h2) {
			font-size: 20px;
		}
	}
}
</style>
